import { GoogleGenAI, GenerationConfig } from "@google/genai";

const apiKey = process.env.REACT_APP_GEMINI_API_KEY || '';
const client = new GoogleGenAI({ apiKey });

// Interfaces for structured output
export interface GeneratedTopic {
    title: string;
    description: string;
    learningPoints: string[];
}

export interface GeneratedQuestion {
    question: string;
    options: string[];
    correctIndex: number;
}

export interface GeneratedMiniSim {
    aiQuestion: string;
    expectedAnswer: string;
    avatarUrl: string;
    voice: string;
}

export interface GeneratedMainSim {
    missions: string[];
    systemInstruction: string;
    userInstruction: string;
    avatarUrl: string;
    voice: string;
}

export interface GeneratedModuleContent {
    questions: GeneratedQuestion[];
    miniSims: GeneratedMiniSim[];
    mainSim: GeneratedMainSim;
}

// Helper to create generation config
const getJsonConfig = (schema: any): GenerationConfig => {
    return {
        responseMimeType: "application/json",
        responseSchema: schema
    } as GenerationConfig;
};

// Helper to try generation with fallback logic
async function generateWithFallback(prompt: string, schema: any) {
    // Priority order: User requested > Latest experimental > Stable fallback
    const candidateModels = ["gemini-3.1-flash-lite-preview", "gemini-2.5-flash", "gemini-2.0-flash-exp"];

    let lastError;

    for (const modelName of candidateModels) {
        try {
            console.log(`Attempting generation with model: ${modelName}`);
            const result = await client.models.generateContent({
                model: modelName,
                contents: [{ role: 'user', parts: [{ text: prompt }] }],
                config: getJsonConfig(schema)
            });
            return result;
        } catch (e: any) {
            console.warn(`Model ${modelName} failed or not found. Trying next...`, e.message);
            lastError = e;
        }
    }
    throw lastError || new Error("All models failed generation.");
}

// 1. Analyze Document to Extract Topics
export async function analyzeDocumentForTopics(docText: string): Promise<GeneratedTopic[]> {
    const prompt = `
Analyze the following training document and identifying distinct topics/sections.
For each topic, provide a title, a brief description, and a list of key learning points (facts/skills to be tested).
The output must be a JSON array of topics.

Document:
${docText}
    `;

    // Schema for topics
    const schema = {
        type: "ARRAY" as any,
        items: {
            type: "OBJECT" as any,
            properties: {
                title: { type: "STRING" as any },
                description: { type: "STRING" as any },
                learningPoints: {
                    type: "ARRAY" as any,
                    items: { type: "STRING" as any }
                }
            },
            required: ["title", "description", "learningPoints"]
        }
    };

    try {
        const result = await generateWithFallback(prompt, schema);

        const responseText = result.text || "";
        if (!responseText) throw new Error("No response from AI");

        return JSON.parse(responseText) as GeneratedTopic[];
    } catch (error) {
        console.error("Topic analysis failed:", error);
        throw error;
    }
}

// 2. Generate Content for a Topic
export async function generateModuleContentForTopic(topic: GeneratedTopic, docText: string): Promise<GeneratedModuleContent> {
    // Using user-provided persona template
    const personaTemplate = `Sen Merve adında, 30 yaşında bir personayı canlandırıyorsun. Bir gün kapın çalıyor ve karşında Turkcell Superonline satış personelini görüyorsun. Bu personelle daha önce bir çok kez konuşmuştunuz fakat yoğun iş temponuz sebebiyle net konuşamamıştınız.
    1 adet öğrenci kızın var ve kızına çok değer veriyorsun. Kızının eğitimi için internet çok önemli.
    Ses tonun: Cana yakın, enerjik
    Karakteristik özelliklerin: Sıcak kanlı, konuşkan, samimi
    Konuşma tarzın: Mesafeli ve üstten bir tavır.
    Mevcut Durumun:
    - İnternet Sağlayıcın: KabloNet
    - Hızın: 25 Mbps
    - Aylık Ödeme: 250 TL
    - Kalan Taahhüt: 9 ay
    - Cayma Bedeli: 2000 TL
    Tavrın ve İtirazların:
    - Şu anki hizmetinden genel olarak 'memnun' olduğunu söyle. (İtirazların: memnunum, taahhüt var, cayma bedeli yüksek, Şu an yeni bir şey almak istemiyorum ihtiyaç duyarsam ben seni ararım.)
    - Aslında şu sorunları yaşıyorsun ama hemen söyleme: Bazen internet yavaşlıyor kızın ders çalışırken aksaklık oluyor.)
    Kurallar:
    - Sorunlarını kendiliğinden dile getirme, satışçı ancak doğru noktaya parmak basarsa dert yan.
    Rolüne sadık kal! Alakasız konuları cevaplama!`;

    const prompt = `
Generate a complete training module for the topic: "${topic.title}".
Context/Learning Points: ${topic.learningPoints.join('; ')}.
Source Document Context: (Use knowledge from here)
${docText.substring(0, 100000)} (truncated if too long)

Requirements:
1. Generate 8 multiple-choice questions (Quiz) testing the learning points.
2. Generate 2 mini-simulation scenarios (MiniSim).
   - "aiQuestion": A question the AI avatar asks the user to test a specific skill (e.g. "How would you handle objection X?").
   - "expectedAnswer": The key points the user should mention.
3. Generate 1 Main Simulation scenario.
   - Use the following Persona Template structure, but ADAPT it to be relevant to the topic "${topic.title}". If the topic is not about internet sales, change the product/context but keep the PERSONA DEPTH (Name, Age, Family, Specific objections, Hidden needs).
   - Template to adapt:
   """${personaTemplate}"""
   - "missions": List of 3-4 specific goals for the user in this simulation.
   - "systemInstruction": The full prompt for the AI agent (adapted persona).
   - "userInstruction": Brief instruction for the user before starting.

Output JSON format with 'questions', 'miniSims', and 'mainSim'.
    `;

    const schema = {
        type: "OBJECT" as any,
        properties: {
            questions: {
                type: "ARRAY" as any,
                items: {
                    type: "OBJECT" as any,
                    properties: {
                        question: { type: "STRING" as any },
                        options: { type: "ARRAY" as any, items: { type: "STRING" as any } },
                        correctIndex: { type: "INTEGER" as any }
                    },
                    required: ["question", "options", "correctIndex"]
                }
            },
            miniSims: {
                type: "ARRAY" as any,
                items: {
                    type: "OBJECT" as any,
                    properties: {
                        aiQuestion: { type: "STRING" as any },
                        expectedAnswer: { type: "STRING" as any },
                        avatarUrl: { type: "STRING" as any, nullable: true }, // Optional, will default
                        voice: { type: "STRING" as any, nullable: true }
                    },
                    required: ["aiQuestion", "expectedAnswer"]
                }
            },
            mainSim: {
                type: "OBJECT" as any,
                properties: {
                    missions: { type: "ARRAY" as any, items: { type: "STRING" as any } },
                    systemInstruction: { type: "STRING" as any },
                    userInstruction: { type: "STRING" as any },
                    avatarUrl: { type: "STRING" as any, nullable: true },
                    voice: { type: "STRING" as any, nullable: true }
                },
                required: ["missions", "systemInstruction", "userInstruction"]
            }
        },
        required: ["questions", "miniSims", "mainSim"]
    };

    try {
        const result = await generateWithFallback(prompt, schema);

        const responseText = result.text || "";
        if (!responseText) throw new Error("No response from AI");

        return JSON.parse(responseText) as GeneratedModuleContent;
    } catch (error) {
        console.error("Module generation failed:", error);
        throw error;
    }
}
